import axios from 'axios';
import { API_CONFIG } from '../config';
import { getPartnerApiToken } from '../auth/AuthContext';
import { notifySessionExpired } from './sessionExpiry';

// ---------------------------------------------------------------------------
// Partner API client
//
// Wraps calls to the partner API (/v1/*) from the Dev Portal browser. The
// Dev Portal authenticates to the *developer* API with a Cognito JWT, but
// the partner API requires an sk_* / st_* / ssk_* bearer. To bridge the two:
//
//   1. AuthContext.getPartnerApiToken(env) mints a short-lived st_* via
//      GET /developer/playground-token?tenant=<env>&ttl=900 (the Cognito-
//      authenticated developer API).
//   2. This axios instance attaches the minted st_* as the Authorization
//      header on every outgoing /v1/* request.
//
// Per-page env selection: every exported call accepts `env` ('live' | 'test')
// as the first arg. The interceptor reads it from the request config so the
// same client instance can serve both envs concurrently — useful when the
// Keys page is showing one env while a background refetch happens against
// the other.
//
// See:
//   - docs/handoffs/mr-dev-portal-scoped-keys-ui.md § Step 1
//   - PartnerAdminKeyHandler.java for the consumed endpoint contracts
// ---------------------------------------------------------------------------

const partnerApi = axios.create({
  baseURL: API_CONFIG.partnerApiBase,
});

/**
 * Request interceptor: attach a freshly-minted st_* bearer. Exported for
 * direct unit testing of the mint-failure → session-expiry path.
 *
 * Callers pass `env` directly on the request config (a custom property — not
 * part of the axios spec, but axios passes unknown keys through).
 */
export async function attachPartnerToken(config) {
  const env = config.env || 'test';
  let token;
  try {
    token = await getPartnerApiToken(env);
  } catch (e) {
    // The st_* mint is Cognito-authenticated (a raw fetch, NOT this axios
    // instance — so a mint 401 never reaches the response interceptor below).
    // A mint failure is almost always an expired/absent session, so escalate
    // to a clean re-login. Rethrow so the call still rejects for the caller.
    notifySessionExpired();
    throw e;
  }
  config.headers.Authorization = `Bearer ${token}`;
  return config;
}

partnerApi.interceptors.request.use(attachPartnerToken);

/**
 * Response error handler: a 401 from the partner API means the bearer (st_*)
 * was rejected — redirect to a clean re-login. Exported for direct unit
 * testing. Always rethrows.
 */
export function handlePartnerApiError(error) {
  if (error?.response?.status === 401) notifySessionExpired();
  return Promise.reject(error);
}

partnerApi.interceptors.response.use((response) => response, handlePartnerApiError);

// FC-01 (#163): the partner-API list endpoints now return a {data, nextCursor} envelope instead of a
// bare top-level array. Unwrap to the data array; tolerate the old bare-array shape too so this stays
// correct across the deploy window (the dev-portal and the API deploy as separate jobs, either order).
const unwrapList = (r) => (Array.isArray(r.data) ? r.data : r.data?.data ?? []);

// ---------------------------------------------------------------------------
// Scoped keys — /v1/admin/keys/scoped/*
// Backend: PartnerAdminKeyHandler.java
// ---------------------------------------------------------------------------

export const listScopedKeys = (env) =>
  partnerApi.get('/v1/admin/keys/scoped', { env }).then(unwrapList);

export const getScopedKey = (env, keyId) =>
  partnerApi.get(`/v1/admin/keys/scoped/${keyId}`, { env }).then((r) => r.data);

export const createScopedKey = (env, body) =>
  partnerApi.post('/v1/admin/keys/scoped', body, { env }).then((r) => r.data);

export const revokeScopedKey = (env, keyId) =>
  partnerApi.delete(`/v1/admin/keys/scoped/${keyId}`, { env });

// ---------------------------------------------------------------------------
// Admin logs — /v1/admin/logs
// Backend: PartnerAdminLogsHandler.java (replaces deleted /developer/logs/api)
// Response shape: { entries[], truncated, queryDurationMs, tenantId }
// ---------------------------------------------------------------------------

export const getAdminLogs = (env, params) =>
  partnerApi.get('/v1/admin/logs', { env, params }).then((r) => r.data);

// ---------------------------------------------------------------------------
// AppContexts + AccessProfiles — /v1/app-contexts/*
// Backend: PartnerAppContextHandler.java (MR #1)
// Used by the ScopedKeyCreateDialog's "App context" wizard step.
// ---------------------------------------------------------------------------

export const listAppContexts = (env) =>
  partnerApi.get('/v1/app-contexts', { env }).then(unwrapList);

/**
 * Returns the AccessProfile for (contextId, principalId), or null if absent.
 * principalId must be passed in already-prefixed form (e.g. "usr_abc-123").
 * Surfaces a clean null on 404 so the wizard can branch on "needs profile".
 */
export const getAccessProfile = (env, contextId, principalId) =>
  partnerApi
    .get(`/v1/app-contexts/${contextId}/profiles/${principalId}`, { env })
    .then((r) => r.data)
    .catch((e) => (e.response?.status === 404 ? null : Promise.reject(e)));

export const createAccessProfile = (env, contextId, body) =>
  partnerApi.post(`/v1/app-contexts/${contextId}/profiles`, body, { env }).then((r) => r.data);

// ---------------------------------------------------------------------------
// PartnerUsers — /v1/users
// Backend: PartnerUserHandler.java
// MR #2 added `type` field (HUMAN | SERVICE) used by the "Bind to user"
// wizard step to filter humans vs service principals.
// ---------------------------------------------------------------------------

export const listUsers = (env) =>
  partnerApi.get('/v1/users', { env }).then((r) => r.data);

/**
 * Create a SERVICE-type PartnerUser (a "service principal"). Email-less.
 * Used by the wizard's inline "Create new service principal" action.
 */
export const createServicePrincipal = (env, externalId) =>
  partnerApi
    .post('/v1/users', { externalId, type: 'SERVICE' }, { env })
    .then((r) => r.data);

export default partnerApi;
